<script setup lang="ts">
// components
import AppLogo from '~/components/redesign/AppLogo.vue';
import Buttons from '~/components/redesign/Buttons';
import LanguageSwitcherFooter from '~/components/redesign/LanguageSwitcherFooter.vue';
import NavItem from '~/components/redesign/NavItem.vue';
import SocialLinks from '~/components/redesign/SocialLinks.vue';

// stores
import useUIStore from '~/stores/ui';

// composable
import useLocalePathWithSlash from '~/composables/useLocalePathWithSlash';
import { storeToRefs } from 'pinia';

// constants
import { PATHS, LINKS } from '~/constants';

// utils
import isString from '~/utils/isString';

const { isLanguageSelectorVisible } = storeToRefs(useUIStore());

const year = new Date().getFullYear();
const localePathWithSlash = useLocalePathWithSlash();

const COLUMNS = [
  [
    {
      item: PATHS.ABOUT_COMPANY,
      children: [
        PATHS.ABOUT,
        PATHS.CAREERS,
        PATHS.ACES_PROGRAM,
        { ...PATHS.IN_THE_NEWS, i18nKey: 'News' },
        PATHS.BLOG,
        PATHS.MEDIA_KIT,
      ],
    },
  ],
  [
    {
      item: PATHS.RESOURCES,
      children: [
        PATHS.WHITE_PAPERS,
        PATHS.TECHNICAL_PAPERS,
        PATHS.ANALYST_REPORTS,
        PATHS.CASE_STUDIES,
      ],
    },
  ],
  [
    {
      item: PATHS.PARTNERS,
      children: [
        {
          path: PATHS.PARTNERS.path,
          i18nKey: 'Become_a_Partner',
        },
        {
          ...PATHS.PARTNER_PORTAL,
          external: true,
        },
        // PATHS.FIND_DISTRIBUTOR,
      ],
    },
  ],
  [
    {
      item: { path: null, i18nKey: 'Popular_Links' },
      children: [
        { ...PATHS.BACKUP_STORAGE_CALCULATOR, i18nKey: 'Storage_Calculator' },
        { ...PATHS.REQUEST_DEMO, i18nKey: 'Request_a_Demo' },
        { ...PATHS.PRODUCT, i18nKey: 'Product_Overview' },
        PATHS.EVENTS,
        PATHS.GUIDES,
        PATHS.SUPPORT,
      ],
    },
  ],
];

const BOTTOM = [
  PATHS.PRIVACY_POLICY,
  { path: LINKS.DSAR_FORM, i18nKey: 'DSAR_Form' },
  PATHS.TRANSLATION_DISCLAIMER,
  PATHS.LEGAL,
  PATHS.TRUST_CENTER,
];
</script>

<template>
  <footer data-component-name="TheFooter">
    <div class="wrapper">
      <div class="top">
        <ul class="columns">
          <li class="column">
            <AppLogo variant="white" />

            <div class="links">
              <SocialLinks />

              <Buttons.Link
                :to="localePathWithSlash(PATHS.CONTACTS_US.path)"
                fill="outline"
                accent="white"
                size="small"
              >
                {{ $t(PATHS.CONTACTS_US.i18nKey) }}
                <SvgIcon name="arrow-forward" />
              </Buttons.Link>
            </div>
          </li>
        </ul>

        <ul class="columns">
          <li
            v-for="(column, index) of COLUMNS"
            :key="index"
            :class="['column', `column-${index + 1}`]"
          >
            <ul class="nav-list">
              <li
                v-for="{ item, children } in column"
                :key="item.path"
                class="nav-item"
              >
                <strong>{{ $t(item.i18nKey) }}</strong>

                <ul class="sub-list">
                  <li
                    v-for="{ path, i18nKey, external } of children"
                    :key="path"
                    class="sub-item"
                  >
                    <NavItem
                      :to="isString(path) ? localePathWithSlash(path) : path"
                      :external
                      size="medium"
                      variant="reversed"
                    >
                      {{ $t(i18nKey) }}
                    </NavItem>
                  </li>
                </ul>
              </li>
            </ul>
          </li>
        </ul>
      </div>

      <hr />

      <div class="bottom">
        <div class="left">
          <LanguageSwitcherFooter v-if="isLanguageSelectorVisible" />
        </div>

        <ul>
          <li v-for="({ path, i18nKey }, index) in BOTTOM" :key="index">
            <NavItem
              :to="localePathWithSlash(path)"
              external
              size="small"
              variant="reversed"
            >
              {{ $t(i18nKey) }}
            </NavItem>
          </li>

          <li>
            <p>&copy; {{ year }} Object First (US) Inc.</p>
          </li>
        </ul>
      </div>
    </div>
  </footer>
</template>

<style scoped lang="scss">
@import '$/functions/token';
@import '$/mixins/flex';
@import '$/mixins/typography';
@import '$/mixins/size';
@import '$/mixins/media';
@import '$/variables/color-roles';

[data-component-name='TheFooter'] {
  padding: 4rem 2rem;
  background-color: token('surf-cont-dark');

  @include tablet {
    padding: 3rem 2rem;
  }

  @include mobile {
    padding: 2rem 1rem;
  }

  .wrapper {
    @include flex-stretch-sb;
    flex-direction: column;

    @include wrapper(76rem);

    .top {
      margin-bottom: 2rem;

      @include flex-center-sb;
      gap: 5rem;

      @include tablet {
        margin-bottom: 0;
        flex-direction: column;
        gap: 2rem;
      }

      @include mobile {
        gap: 1.5rem;
      }

      ul.columns {
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        grid-template-rows: repeat(1, 1fr);
        gap: 3.6875rem;

        @include tablet {
          gap: 0.5rem;
          width: 100%;
        }

        @include mobile {
          grid-template-columns: repeat(2, 1fr);
          gap: 1rem;
        }

        li.column {
          @include tablet {
            width: 100%;
          }

          ul.nav-list {
            li.nav-item:not(:first-of-type) {
              margin-top: 2rem;
            }
          }

          ul.nav-list {
            @include flex-stretch-start;
            flex-direction: column;

            li.nav-item {
              strong {
                display: block;
                padding: 1rem;

                color: token('text-reversed');
                @include subtitle-3;
              }

              ul.sub-list {
                @include flex-stretch-start;
                flex-direction: column;
              }
            }
          }
        }

        &:first-of-type {
          display: grid;
          grid-template-columns: 1fr;

          align-self: start;

          li.column {
            @include flex-start-start;
            flex-direction: column;

            gap: 2rem;

            @include tablet {
              flex-direction: row;
              @include flex-start-sb;
              gap: 1rem;
            }

            @include mobile {
              flex-direction: column;
              @include flex-start-start;
              gap: 1.5rem;
            }

            [data-component-name='AppLogo'] {
              align-self: flex-start;

              display: block;
              width: 9.25rem;

              @include tablet {
                width: 15.75rem;
              }

              @include mobile {
                width: 9.25rem;
              }
            }

            .links {
              @include flex-start-center;
              flex-direction: column;
              gap: 2rem;

              @include tablet {
                gap: 1rem;

                :deep([data-component-name='Buttons.Link']) {
                  width: 100%;
                }
              }

              @include mobile {
                gap: 1.5rem;

                :deep([data-component-name='Buttons.Link']) {
                  width: auto;
                }
              }
            }
          }
        }

        li {
          @include tablet {
            &.column-1 {
              order: 2;
            }
            &.column-2 {
              order: 3;
            }
            &.column-3 {
              order: 4;
            }
            &.column-4 {
              order: 1;
            }
          }
        }
      }
    }

    hr {
      display: none;

      @include tablet {
        display: block;
        width: 100%;
        margin: 1.5rem 0;
      }

      @include mobile {
      }
    }

    .bottom {
      @include flex-center-sb;

      @include tablet {
        flex-direction: column;
        @include flex-start-start;
        gap: 0.5rem;
      }

      @include mobile {
        gap: 1rem;
      }

      ul {
        @include flex-center-center;
        flex-direction: row;
        gap: 0.5rem;

        @include tablet {
          flex-wrap: wrap;
          @include flex-center-start;
        }
      }

      p {
        @include body-4;
        color: token('text-reversed');
        padding-inline: 1rem;
      }
    }
  }
}
</style>
